/**
 * DSBridge 原生方法封装工具类
 * 统一管理所有原生交互方法
 */
import dsBridge from 'dsbridge'
import { saveOcrApiLog } from '@/api/ocr'
import ConstData from '@/constants/ConstData'
import debugLogger from '@/utils/debugLogger'

class DSBridgeHelper {
  constructor() {
    this.bridge = dsBridge
    this._platform = null
    this._ocrInitContext = this._createEmptyOcrInitContext()
  }

  /**
   * 解析原生返回的 JSON 字符串
   * @param {string|Object} result 原生返回值
   * @returns {Object}
   */
  _parseResult(result) {
    if (result === undefined || result === null || result === '') return result
    if (typeof result === 'object') return result
    if (typeof result === 'string') {
      try {
        return JSON.parse(result)
      } catch (e) {
        return result
      }
    }
    return result
  }

  /**
   * 统一序列化返回结果，便于调试输出
   * @param {*} payload 任意内容
   * @returns {string}
   */
  _stringifyResult(payload) {
    try {
      return JSON.stringify(payload, null, 2)
    } catch (e) {
      return String(payload)
    }
  }

  /**
   * 深度移除 OCR 日志中的大字段和敏感字段，避免请求日志泄露凭证
   * @param {*} payload 原始数据
   * @param {WeakMap<object, *>} visited 已访问对象缓存
   * @returns {*}
   */
  _sanitizeOcrReportPayload(payload, visited = new WeakMap()) {
    if (payload === null || payload === undefined) {
      return payload
    }

    if (typeof payload !== 'object') {
      return payload
    }

    if (visited.has(payload)) {
      return visited.get(payload)
    }

    if (Array.isArray(payload)) {
      const clonedArray = []
      visited.set(payload, clonedArray)

      payload.forEach(item => {
        clonedArray.push(this._sanitizeOcrReportPayload(item, visited))
      })

      return clonedArray
    }

    const clonedObject = {}
    visited.set(payload, clonedObject)

    Object.keys(payload).forEach(key => {
      // OCR 返回里的 base64 图片过大，敏感凭证也不应该进入控制台和日志上报。
      if (
        key === 'imageBase64' ||
        key === 'account_id' ||
        key === 'accountId' ||
        key === 'app_key' ||
        key === 'appKey'
      ) {
        return
      }

      clonedObject[key] = this._sanitizeOcrReportPayload(payload[key], visited)
    })

    return clonedObject
  }

  /**
   * 打印 OCR 调用日志，便于排查原生桥接入参与调用时机
   * @param {string} action 动作名称
   * @param {Object} payload 调用上下文
   */
  _logOcrCall(action, payload = {}) {
    debugLogger.log(`[utils/dsBridgeHelper/ocr] ${action}`, this._sanitizeOcrReportPayload(payload))
  }

  /**
   * 打印 OCR 调用结果日志，包含原始返回值与解析后结果
   * @param {string} action 动作名称
   * @param {Object} payload 调用结果上下文
   */
  _logOcrResult(action, payload = {}) {
    debugLogger.log(`[utils/dsBridgeHelper/ocr] ${action} result`, this._sanitizeOcrReportPayload(payload))
  }

  /**
   * 兼容原生返回的真假值
   * @param {*} value 原生返回值
   * @returns {boolean}
   */
  _isTruthyAppFlag(value) {
    return value === true || value === 'true' || value === 1 || value === '1'
  }

  /**
   * 创建空的 OCR 初始化上下文
   * @param {Object|null} request 初始化请求参数
   * @returns {Object}
   */
  _createEmptyOcrInitContext(request = null) {
    return {
      request,
      response: null,
      reqId: '',
      token: ''
    }
  }

  /**
   * 递归查找对象中的指定字段
   * @param {*} payload 数据源
   * @param {string[]} keys 目标字段名
   * @param {WeakSet} visited 已访问对象
   * @returns {*}
   */
  _findNestedValue(payload, keys = [], visited = new WeakSet()) {
    if (payload === undefined || payload === null || payload === '') {
      return ''
    }

    if (typeof payload !== 'object') {
      return ''
    }

    if (visited.has(payload)) {
      return ''
    }

    visited.add(payload)

    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key]
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
    }

    const values = Array.isArray(payload) ? payload : Object.values(payload)
    for (const value of values) {
      const matchedValue = this._findNestedValue(value, keys, visited)
      if (matchedValue !== undefined && matchedValue !== null && matchedValue !== '') {
        return matchedValue
      }
    }

    return ''
  }

  /**
   * 提取 OCR 上报需要的 req_id 与 token
   * @param {*} payload 返回数据
   * @returns {{reqId: string, token: string}}
   */
  _extractOcrReportIdentity(payload) {
    return {
      reqId: this._findNestedValue(payload, ['req_id', 'reqId']),
      token: this._findNestedValue(payload, ['token', 'auth_token', 'authToken'])
    }
  }

  /**
   * 缓存 OCR 初始化上下文，供正反面识别上报复用
   * @param {Object} requestParams 初始化请求参数
   * @param {*} response 初始化返回数据
   */
  _cacheOcrInitContext(requestParams = {}, response = null) {
    const { reqId, token } = this._extractOcrReportIdentity(response)

    this._ocrInitContext = {
      request: requestParams,
      response,
      reqId: reqId || '',
      token: token || ''
    }
  }

  /**
   * 构建 OCR 上报请求体
   * @param {string} action 当前动作
   * @param {Object} requestParams 当前请求参数
   * @param {*} response 当前返回数据
   * @returns {Object|null}
   */
  _buildOcrApiLogPayload(action, requestParams = {}, response = null) {
    const { reqId, token } = this._extractOcrReportIdentity(response)
    const finalReqId = reqId || this._ocrInitContext.reqId || ''
    const finalToken = token || this._ocrInitContext.token || ''
    const sanitizedResponse = this._sanitizeOcrReportPayload(response)

    // if (!finalReqId || !finalToken) {
    //   return null
    // }

    const reqContent =
      this._stringifyResult(
        this._sanitizeOcrReportPayload({
          initOcrRequest: this._ocrInitContext.request,
          action,
          request: requestParams
        })
      ) || ''

    const respContent = this._stringifyResult(sanitizedResponse)

    return {
      req_id: finalReqId,
      token: finalToken,
      caller_type: 1,
      api_type: ConstData.EKYC_DYNA_API_LOG_API_TYPE.OCR,
      req_content: reqContent === undefined ? '' : reqContent,
      resp_content: respContent === undefined ? '' : respContent
    }
  }

  /**
   * OCR 正反面识别结果静默上报
   * @param {string} action 当前动作
   * @param {Object} requestParams 当前请求参数
   * @param {*} response 当前返回数据
   */
  _reportOcrApiLog(action, requestParams = {}, response = null) {
    const payload = this._buildOcrApiLogPayload(action, requestParams, response)
    //
    // if (!payload) {
    //   this._logOcrCall('saveApiLog skipped', {s
    //     action,
    //     reason: '缺少 req_id 或 token'
    //   })
    //   return
    // }

    this._ocrInitContext = {
      ...this._ocrInitContext,
      reqId: payload.req_id,
      token: payload.token
    }

    saveOcrApiLog(payload)
      .then(() => {})
      .catch(error => {
        debugLogger.exception('[utils/dsBridgeHelper/ocr] saveApiLog fail', error, {
          action,
          reqId: payload.req_id
        })
      })
  }

  /**
   * 检查是否在 APP 内
   */
  isApp() {
    if (!this.bridge || !this.bridge.call) {
      return false
    }

    try {
      const appFlag = this.bridge.call('sync.isApp', '')

      if (appFlag !== undefined && appFlag !== null && appFlag !== '') {
        return this._isTruthyAppFlag(appFlag)
      }
    } catch (e) {
      debugLogger.exception('[utils/dsBridgeHelper] sync.isApp 调用失败', e)
    }

    return !!(
      (typeof window !== 'undefined' && (window._dsbridge || window._dswk)) ||
      (typeof navigator !== 'undefined' && navigator.userAgent.indexOf('_dsbridge') !== -1)
    )
  }

  /**
   * 检查原生方法是否存在
   */
  hasNativeMethod(methodName) {
    try {
      return this.bridge && this.bridge.hasNativeMethod(methodName)
    } catch (e) {
      return false
    }
  }

  /**
   * 统一调用 dsBridge 异步方法
   * @param {string} methodName 原生方法名
   * @param {Object} params 调用参数
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  callDsBridgeAsync(methodName, params = {}, options = {}) {
    const {
      timeout = 3000000,
      requireApp = true,
      checkMethod = true,
      debugLogResult = false,
      debugAction = methodName
    } = options

    return new Promise((resolve, reject) => {
      if (!this.bridge || !this.bridge.call) {
        reject(new Error('dsBridge 不可用'))
        return
      }

      if (requireApp && !this.isApp()) {
        reject(new Error('当前不在 App 原生容器，无法调用原生方法'))
        return
      }

      if (checkMethod && this.bridge.hasNativeMethod && !this.hasNativeMethod(methodName)) {
        reject(new Error(`原生方法不存在：${methodName}`))
        return
      }

      let finished = false
      const timer = setTimeout(() => {
        if (!finished) {
          finished = true
          reject(new Error(`调用超时：${methodName}`))
        }
      }, timeout)

      const finishResolve = result => {
        if (finished) {
          return
        }

        finished = true
        clearTimeout(timer)
        const parsedResult = this._parseResult(result)

        if (debugLogResult) {
          this._logOcrResult(debugAction, {
            methodName,
            rawResult: result,
            parsedResult
          })
        }

        resolve(parsedResult)
      }

      try {
        const syncResult = this.bridge.call(methodName, params, res => {
          finishResolve(res)
        })

        if (syncResult !== undefined && syncResult !== null && syncResult !== '') {
          finishResolve(syncResult)
        }
      } catch (error) {
        clearTimeout(timer)
        reject(error)
      }
    })
  }

  // ==================== OCR 相关 ====================

  /**
   * OCR 默认初始化配置
   * @param {Object} config 自定义配置
   * @param {string} appLanguage 项目语言
   * @returns {Object}
   */
  getDefaultOcrConfig(config = {}, appLanguage = 'en') {
    const { language, ...restConfig } = config

    return {
      isDoubleType: false,
      overBlurThreshold: 2.5,
      overDarkThreshold: 80.0,
      overGlareThreshold: 200.0,
      language: language || this.getOcrLanguageByAppLanguage(appLanguage),
      timeout: 20000,
      ...restConfig
    }
  }

  /**
   * 将项目语言映射为 OCR 语言枚举
   * @param {string} appLanguage 项目语言
   * @returns {string}
   */
  getOcrLanguageByAppLanguage(appLanguage = 'en') {
    const ocrLanguageMap = {
      zh: 'CHINESE',
      en: 'ENGLISH',
      th: 'THAI',
      id: 'INDONESIAN',
      fil: 'FILIPINO',
      vi: 'VIETNAMESE',
      es: 'SPANISH'
    }

    return ocrLanguageMap[appLanguage] || 'ENGLISH'
  }

  /**
   * 获取当前桥接环境文案
   * @returns {string}
   */
  getBridgeEnvText() {
    return this.isApp() ? 'App 原生容器' : 'H5 / 非原生容器'
  }

  /**
   * 获取 OCR 背面识别对应的原生方法
   * @returns {string}
   */
  getOcrBackMethodName() {
    return this.hasNativeMethod('async.ocrBack') ? 'async.ocrBack' : 'async.completeOcr'
  }

  /**
   * 格式化 OCR 测试返回结果
   * @param {string} action 动作名称
   * @param {Object} payload 返回数据
   * @param {boolean} isError 是否失败
   * @returns {Object}
   */
  buildOcrTestResult(action, payload, isError = false) {
    return {
      action,
      success: !isError,
      time: new Date().toLocaleString(),
      payload
    }
  }

  /**
   * 将 OCR 结果格式化为字符串
   * @param {Object} payload 返回数据
   * @returns {string}
   */
  stringifyOcrResult(payload) {
    return this._stringifyResult(payload)
  }

  /**
   * 初始化 OCR
   * @param {Object} config OCR 初始化配置
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  initOcr(config = {}, options = {}) {
    const { appLanguage = 'en', ...callOptions } = options
    const requestParams = {
      config: this.getDefaultOcrConfig(config, appLanguage)
    }
    const bridgeOptions = {
      ...callOptions,
      debugAction: 'initOcr'
    }

    this._ocrInitContext = this._createEmptyOcrInitContext(requestParams)

    return this.callDsBridgeAsync('async.initOcr', requestParams, bridgeOptions).then(result => {
      this._cacheOcrInitContext(requestParams, result)
      this._reportOcrApiLog('initOcr', requestParams, result)
      return result
    })
  }

  /**
   * 按项目语言初始化 OCR
   * @param {string} appLanguage 项目语言
   * @param {Object} config OCR 初始化配置
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  initOcrByLanguage(appLanguage = 'en', config = {}, options = {}) {
    return this.initOcr(config, {
      ...options,
      appLanguage
    })
  }

  /**
   * 识别证件正面
   * @param {Object} params 调用参数
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  ocrFront(params = {}, options = {}) {
    const requestParams = {
      orientation: 'landscape',
      includeImageBase64: true,
      ...params
    }
    const bridgeOptions = {
      ...options,
      debugAction: 'ocrFront'
    }

    return this.callDsBridgeAsync('async.ocrFront', requestParams, bridgeOptions).then(result => {
      this._reportOcrApiLog('ocrFront', requestParams, result)
      return result
    })
  }

  /**
   * 调用 OCR 完成方法
   * @param {Object} params 调用参数
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  completeOcr(params = {}, options = {}) {
    const requestParams = {
      ...params
    }
    const bridgeOptions = {
      ...options,
      debugAction: 'completeOcr'
    }

    this._logOcrCall('completeOcr', {
      methodName: 'async.completeOcr',
      requestParams
    })

    return this.callDsBridgeAsync('async.completeOcr', requestParams, bridgeOptions).then(result => {
      this._logOcrResult('completeOcr', {
        methodName: 'async.completeOcr',
        parsedResult: result
      })
      this._reportOcrApiLog('completeOcr', requestParams, result)
      return result
    })
  }

  /**
   * 识别证件背面，优先使用 async.ocrBack，不存在时回退到 async.completeOcr
   * @param {Object} params 调用参数
   * @param {Object} options 附加配置
   * @returns {Promise<*>}
   */
  ocrBack(params = {}, options = {}) {
    const methodName = this.getOcrBackMethodName()
    const reportAction = methodName === 'async.completeOcr' ? 'completeOcr' : 'ocrBack'
    const requestParams =
      methodName === 'async.completeOcr'
        ? params
        : {
            orientation: 'landscape',
            includeImageBase64: true,
            ...params
          }
    const bridgeOptions = {
      ...options,
      debugAction: reportAction
    }

    if (methodName === 'async.completeOcr') {
      this._logOcrCall('ocrBack fallback to completeOcr', {
        methodName,
        requestParams
      })
    }

    return this.callDsBridgeAsync(methodName, requestParams, bridgeOptions).then(result => {
      if (methodName === 'async.completeOcr') {
        this._logOcrResult('ocrBack fallback completeOcr', {
          methodName,
          parsedResult: result
        })
      }
      this._reportOcrApiLog(reportAction, requestParams, result)
      return result
    })
  }

  /**
   * 通用 OCR 测试执行器，返回结构化结果供页面自行展示
   * @param {Object} options 测试配置
   * @returns {Promise<Object>}
   */
  async runOcrTest(options = {}) {
    const {
      actionLabel,
      methodName,
      params = {},
      successMessage = '调用成功',
      cancelMessage = '用户取消操作',
      failMessage = '调用失败',
      callOptions = {}
    } = options

    try {
      const result = await this.callDsBridgeAsync(methodName, params, callOptions)
      const status = result && result.status
      const payload = {
        methodName,
        params,
        result
      }
      const formatted = this.buildOcrTestResult(actionLabel, payload)

      return {
        success: status === 'success',
        status,
        message: status === 'success' ? successMessage : status === 'cancelled' ? cancelMessage : failMessage,
        actionLabel,
        methodName,
        params,
        result,
        formatted,
        formattedText: this.stringifyOcrResult(formatted)
      }
    } catch (error) {
      const errorMessage = error && error.message ? error.message : String(error)
      const payload = {
        methodName,
        params,
        error: errorMessage
      }
      const formatted = this.buildOcrTestResult(actionLabel, payload, true)

      return {
        success: false,
        status: 'error',
        message: errorMessage,
        actionLabel,
        methodName,
        params,
        error: errorMessage,
        formatted,
        formattedText: this.stringifyOcrResult(formatted)
      }
    }
  }

  /**
   * 测试 OCR 初始化
   * @param {Object} config 初始化配置
   * @param {Object} callOptions 附加调用配置
   * @returns {Promise<Object>}
   */
  testInitOcr(config = {}, callOptions = {}) {
    const { appLanguage = 'en', ...restCallOptions } = callOptions

    return this.runOcrTest({
      actionLabel: 'OCR 初始化',
      methodName: 'async.initOcr',
      params: {
        config: this.getDefaultOcrConfig(config, appLanguage)
      },
      successMessage: 'OCR 初始化成功',
      failMessage: 'OCR 初始化失败',
      callOptions: restCallOptions
    })
  }

  /**
   * 测试 OCR 正面识别
   * @param {Object} params 识别参数
   * @param {Object} callOptions 附加调用配置
   * @returns {Promise<Object>}
   */
  testOcrFront(params = {}, callOptions = {}) {
    return this.runOcrTest({
      actionLabel: 'OCR 正面识别',
      methodName: 'async.ocrFront',
      params: {
        orientation: 'landscape',
        includeImageBase64: true,
        ...params
      },
      successMessage: '证件正面识别成功',
      cancelMessage: '用户取消正面识别',
      failMessage: '证件正面识别失败',
      callOptions
    })
  }

  /**
   * 测试 OCR 背面识别
   * @param {Object} params 识别参数
   * @param {Object} callOptions 附加调用配置
   * @returns {Promise<Object>}
   */
  testOcrBack(params = {}, callOptions = {}) {
    const methodName = this.getOcrBackMethodName()
    const requestParams =
      methodName === 'async.completeOcr'
        ? params
        : {
            orientation: 'landscape',
            includeImageBase64: true,
            ...params
          }

    return this.runOcrTest({
      actionLabel: 'OCR 背面识别',
      methodName,
      params: requestParams,
      successMessage: methodName === 'async.completeOcr' ? 'OCR 完成成功' : '证件背面识别成功',
      cancelMessage: '用户取消背面识别',
      failMessage: methodName === 'async.completeOcr' ? 'OCR 完成失败' : '证件背面识别失败',
      callOptions
    })
  }

  /**
   * 获取平台类型（基于 UserAgent）
   * @returns {'ios'|'android'|'web'|'unknown'}
   */
  getPlatform() {
    if (this._platform) {
      return this._platform
    }

    if (!this.isApp()) {
      this._platform = 'web'
      return this._platform
    }

    // 基于 UserAgent 判断设备类型
    const ua = navigator.userAgent.toLowerCase()
    if (/iphone|ipad|ipod/.test(ua)) {
      this._platform = 'ios'
    } else if (/android/.test(ua)) {
      this._platform = 'android'
    } else {
      this._platform = 'unknown'
    }

    return this._platform
  }

  /**
   * 检查是否为 iOS 平台
   */
  isIOS() {
    return this.getPlatform() === 'ios'
  }

  /**
   * 检查是否为 Android 平台
   */
  isAndroid() {
    return this.getPlatform() === 'android'
  }

  // ==================== 权限相关 ====================

  /**
   * 异步检查相机权限。
   * 当前只支持新版原生显式查询方法。
   * @returns {Promise<boolean>}
   */
  async checkCameraPermissionAsync() {
    if (!this.isApp()) {
      return false
    }

    if (!this.hasNativeMethod('sync.checkCameraPermission')) {
      return false
    }

    try {
      const result = this._parseResult(this.bridge.call('sync.checkCameraPermission', '{}'))

      if (result && typeof result === 'object' && result.granted !== undefined) {
        return this._isTruthyAppFlag(result.granted)
      }

      return this._isTruthyAppFlag(result)
    } catch (error) {
      debugLogger.exception('[utils/dsBridgeHelper] checkCameraPermissionAsync failed', error)
      return false
    }
  }

  /**
   * 异步检查定位权限。
   * @returns {Promise<boolean>}
   */
  checkLocationPermissionAsync() {
    return new Promise(resolve => {
      if (!this.isApp()) {
        resolve(false)
        return
      }

      if (this.hasNativeMethod('async.checkLocationPermission')) {
        this.bridge.call('async.checkLocationPermission', '', res => {
          const result = this._parseResult(res)
          if (result && typeof result === 'object' && result.granted !== undefined) {
            resolve(this._isTruthyAppFlag(result.granted))
            return
          }

          resolve(this._isTruthyAppFlag(result))
        })
        return
      }

      resolve(false)
    })
  }

  /**
   * 请求相机权限
   * @returns {Promise<boolean>}
   */
  requestCameraPermission() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('async.requestCameraPermission', '', res => {
        const result = this._parseResult(res)
        if (result && result.granted !== undefined) {
          resolve(result.granted)
        } else {
          reject(new Error('Permission request failed'))
        }
      })
    })
  }

  /**
   * 请求相册权限
   * @returns {Promise<boolean>}
   */
  requestPhotoLibraryPermission() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('async.requestPhotoLibraryPermission', '', res => {
        const result = this._parseResult(res)
        if (result && result.granted !== undefined) {
          resolve(result.granted)
        } else {
          reject(new Error('Permission request failed'))
        }
      })
    })
  }

  /**
   * 请求录音权限
   * @returns {Promise<boolean>}
   */
  requestMicrophonePermission() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('async.requestMicrophonePermission', '', res => {
        const result = this._parseResult(res)
        if (result && result.granted !== undefined) {
          resolve(result.granted)
        } else {
          reject(new Error('Permission request failed'))
        }
      })
    })
  }

  // ==================== 图片相关 ====================

  /**
   * 打开图片选择器（相机或相册）
   * @param {Object} options - { source: 'camera'|'photo', allowsEditing: boolean }
   * @returns {Promise<Object>} { path: string, name: string }
   */
  openImagePicker(options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const params = {
        source: options.source || 'photo', // 'camera' 或 'photo'
        allowsEditing: options.allowsEditing || false
      }

      this.bridge.call('async.openImagePicker', params, res => {
        const result = this._parseResult(res)
        if (result && result.path) {
          resolve(result)
        } else if (result && result.cancelled) {
          reject(new Error('User cancelled'))
        } else if (result && result.error) {
          reject(new Error(result.error))
        } else {
          reject(new Error('No image selected'))
        }
      })
    })
  }

  /**
   * 打开相机拍照（快捷方法，兼容旧版）
   * @returns {Promise<Object>} { path, name, imageData, isBase64 }
   */
  async openCamera() {
    const platform = this.getPlatform()

    // Android 旧方法兼容
    if (platform === 'android' && this.hasNativeMethod('CameraJsApi.openActivity')) {
      return new Promise((resolve, reject) => {
        this.bridge.call('CameraJsApi.openActivity', {}, res => {
          const ret = this._parseResult(res)
          if (ret && ret.img) {
            // Android 返回 base64
            resolve({
              imageData: ret.img,
              isBase64: true,
              platform: 'android-legacy'
            })
          } else {
            reject(new Error('No image data'))
          }
        })
      })
    }

    // iOS/新方法
    return this.openImagePicker({ source: 'camera' })
  }

  /**
   * 打开相册选择图片（快捷方法，兼容旧版）
   * @returns {Promise<Object>} { path, name, imageData, isBase64 }
   */
  async openAlbum() {
    const platform = this.getPlatform()

    // Android 旧方法兼容
    if (platform === 'android' && this.hasNativeMethod('AlbumJsApi.openActivity')) {
      return new Promise((resolve, reject) => {
        this.bridge.call('AlbumJsApi.openActivity', {}, res => {
          const ret = this._parseResult(res)
          if (ret && ret.img) {
            // Android 返回 base64
            resolve({
              imageData: ret.img,
              isBase64: true,
              platform: 'android-legacy'
            })
          } else {
            reject(new Error('No image data'))
          }
        })
      })
    }

    // iOS/新方法
    return this.openImagePicker({ source: 'photo' })
  }

  /**
   * 打开文件选择器
   * @param {Object} options - { type: 'image'|'pdf'|'video'|'audio'|'text'|'any' }
   * @returns {Promise<Object>} { path: string, name: string }
   */
  openFilePicker(options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const params = {
        type: options.type || 'any'
      }

      this.bridge.call('async.openFilePicker', params, res => {
        const result = this._parseResult(res)
        if (result && result.path) {
          resolve(result)
        } else if (result && result.cancelled) {
          reject(new Error('User cancelled'))
        } else if (result && result.error) {
          reject(new Error(result.error))
        } else {
          reject(new Error('No file selected'))
        }
      })
    })
  }

  /**
   * 压缩并上传图片（原生实现）
   * @param {Object} options 配置项
   * @returns {Promise<Object>} 上传结果
   */
  compressAndUploadImage(options) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const defaultOptions = {
        imageData: '',
        quality: 0.8,
        maxWidth: 1920,
        maxHeight: 1920,
        uploadUrl: '',
        business: 'WALLET_USER_VOUCHER',
        needDecrypt: false,
        headers: {}
      }

      const params = { ...defaultOptions, ...options }

      this.bridge.call('ImageJsApi.compressAndUpload', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result)
        } else {
          reject(new Error(result.message || 'Upload failed'))
        }
      })
    })
  }

  /**
   * 保存图片到相册
   * @param {string} imageData base64 或 URL
   * @param {string} fileName 文件名
   * @returns {Promise<Object>}
   */
  saveImageToGallery(imageData, fileName = 'image.png') {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call(
        'FileJsApi.saveImageToGallery',
        {
          imageData,
          fileName
        },
        res => {
          const result = this._parseResult(res)
          if (result && result.success) {
            resolve(result)
          } else {
            reject(new Error(result.message || 'Save failed'))
          }
        }
      )
    })
  }

  // ==================== 人脸识别相关 ====================

  /**
   * 启动原生人脸识别
   * @param {Object} options 配置项
   * @returns {Promise<Object>}
   */
  async startFaceDetection(options = {}) {
    if (!this.isApp()) {
      throw new Error('Not in app')
    }

    // 使用异步方式检查相机权限
    const hasPermission = await this.checkCameraPermissionAsync()
    if (!hasPermission) {
      await this.requestCameraPermission()
      throw new Error('Camera permission denied')
    }

    return new Promise((resolve, reject) => {
      const defaultOptions = {
        type: 'liveness', // liveness（活体检测）或 compare（人脸比对）
        referenceImage: '', // 参考图片（比对模式）
        config: {
          numOfLiveness: 1,
          actions: [0, 1, 2] // 0:眨眼 1:张嘴 2:摇头
        }
      }

      const params = { ...defaultOptions, ...options }

      this.bridge.call('FaceJsApi.startFaceDetection', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result)
        } else {
          reject(new Error(result.message || 'Face detection failed'))
        }
      })
    })
  }

  // ==================== 二维码相关 ====================

  /**
   * 扫描二维码
   * @returns {Promise<string>} 二维码内容
   */
  async scanQRCode() {
    if (!this.isApp()) {
      throw new Error('Not in app')
    }

    // 使用异步方式检查相机权限
    const hasPermission = await this.checkCameraPermissionAsync()
    if (!hasPermission) {
      await this.requestCameraPermission()
      throw new Error('Camera permission denied')
    }

    return new Promise((resolve, reject) => {
      this.bridge.call('QRCodeJsApi.scanQRCode', {}, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result.content)
        } else {
          reject(new Error(result.message || 'Scan failed'))
        }
      })
    })
  }

  /**
   * 识别图片中的二维码
   * @param {string} imageData base64 图片数据
   * @returns {Promise<string>} 二维码内容
   */
  recognizeQRCode(imageData) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('QRCodeJsApi.recognizeQRCode', { imageData }, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result.content)
        } else {
          reject(new Error(result.message || 'Recognize failed'))
        }
      })
    })
  }

  // ==================== 生物识别相关 ====================

  /**
   * 检查生物识别可用性
   * @returns {Promise<Object>} { available: boolean, type: 'fingerprint'|'face'|'none' }
   */
  checkBiometricAvailable() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('BiometricJsApi.isAvailable', {}, res => {
        const result = this._parseResult(res)
        resolve(result || { available: false, type: 'none' })
      })
    })
  }

  /**
   * 进行生物识别验证
   * @param {Object} options 配置项
   * @returns {Promise<boolean>}
   */
  authenticateBiometric(options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const defaultOptions = {
        title: '验证身份',
        subtitle: '请使用指纹或面容验证',
        description: '确认操作'
      }

      const params = { ...defaultOptions, ...options }

      this.bridge.call('BiometricJsApi.authenticate', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(true)
        } else {
          reject(new Error((result && result.error) || 'Authentication failed'))
        }
      })
    })
  }

  // ==================== 剪贴板相关 ====================

  /**
   * 复制文本到剪贴板
   * @param {string} text 要复制的文本
   * @returns {Promise<boolean>}
   */
  copyToClipboard(text) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        // 降级到 H5 方案
        this._copyTextH5(text).then(resolve).catch(reject)
        return
      }

      this.bridge.call('ClipboardJsApi.copy', { text }, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(true)
        } else {
          reject(new Error('Copy failed'))
        }
      })
    })
  }

  /**
   * 读取剪贴板内容
   * @returns {Promise<string>}
   */
  pasteFromClipboard() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('ClipboardJsApi.paste', {}, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result.text)
        } else {
          reject(new Error('Paste failed'))
        }
      })
    })
  }

  /**
   * H5 复制方案（降级）
   */
  _copyTextH5(text) {
    return new Promise((resolve, reject) => {
      if (!text) {
        reject(new Error('No text to copy'))
        return
      }

      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        const successful = document.execCommand('copy')
        if (successful) {
          resolve(true)
        } else {
          reject(new Error('Copy command failed'))
        }
      } catch (err) {
        reject(err)
      } finally {
        document.body.removeChild(textarea)
      }
    })
  }

  // ==================== 文件下载相关 ====================

  /**
   * 下载文件
   * @param {Object} options 配置项
   * @returns {Promise<Object>}
   */
  downloadFile(options) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const defaultOptions = {
        url: '',
        fileName: 'download',
        showNotification: true
      }

      const params = { ...defaultOptions, ...options }

      this.bridge.call('FileJsApi.downloadFile', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(result)
        } else {
          reject(new Error((result && result.message) || 'Download failed'))
        }
      })
    })
  }

  // ==================== 设备信息相关（同步方法）====================

  /**
   * 获取完整设备信息（同步）
   * @returns {Object}
   */
  getDeviceInfo() {
    if (!this.isApp()) {
      return {}
    }
    const result = this.bridge.call('sync.getDeviceInfo', '')
    if (!result) {
      return {}
    }
    try {
      return JSON.parse(result)
    } catch (e) {
      debugLogger.exception('[utils/dsBridgeHelper] getDeviceInfo parse error', e)
      return {}
    }
  }

  /**
   * 获取 App 信息（同步）
   * @returns {Object} { name, displayName, bundleId, version, build }
   */
  getAppInfo() {
    if (!this.isApp()) {
      return {}
    }
    return this.bridge.call('sync.getAppInfo', '') || {}
  }

  /**
   * 获取 APP 版本号（同步）
   * @returns {string}
   */
  getAppVersion() {
    if (!this.isApp()) {
      return ''
    }
    return this.bridge.call('sync.getAppVersion', '') || ''
  }

  /**
   * 获取 App Bundle ID（同步）
   * @returns {string}
   */
  getAppBundleId() {
    if (!this.isApp()) {
      return ''
    }
    return this.bridge.call('sync.getAppBundleId', '') || ''
  }

  /**
   * 获取系统版本（同步）
   * @returns {string}
   */
  getSystemVersion() {
    if (!this.isApp()) {
      return ''
    }
    return this.bridge.call('sync.getSystemVersion', '') || ''
  }

  /**
   * 获取设备型号（同步）
   * @returns {string}
   */
  getDeviceModel() {
    if (!this.isApp()) {
      return ''
    }
    return this.bridge.call('sync.getDeviceModel', '') || ''
  }

  /**
   * 获取设备名称（同步）
   * @returns {string}
   */
  getDeviceName() {
    if (!this.isApp()) {
      return ''
    }
    return this.bridge.call('sync.getDeviceName', '') || ''
  }

  // ==================== 安全区信息（同步方法）====================

  /**
   * 获取顶部安全区高度（同步）
   * @returns {number}
   */
  getSafeAreaTop() {
    if (!this.isApp()) {
      return 0
    }
    return this.bridge.call('sync.getSafeAreaTop', '') || 0
  }

  /**
   * 获取底部安全区高度（同步）
   * @returns {number}
   */
  getSafeAreaBottom() {
    if (!this.isApp()) {
      return 0
    }
    return this.bridge.call('sync.getSafeAreaBottom', '') || 0
  }

  /**
   * 获取所有安全区信息（同步）
   * @returns {Object} { top, bottom, left, right }
   */
  getSafeAreaInsets() {
    if (!this.isApp()) {
      return { top: 0, bottom: 0, left: 0, right: 0 }
    }
    return this.bridge.call('sync.getSafeAreaInsets', '') || { top: 0, bottom: 0, left: 0, right: 0 }
  }

  // ==================== 屏幕信息（同步方法）====================

  /**
   * 获取屏幕宽度（同步）
   * @returns {number}
   */
  getScreenWidth() {
    if (!this.isApp()) {
      return window.innerWidth || 0
    }
    return this.bridge.call('sync.getScreenWidth', '') || 0
  }

  /**
   * 获取屏幕高度（同步）
   * @returns {number}
   */
  getScreenHeight() {
    if (!this.isApp()) {
      return window.innerHeight || 0
    }
    return this.bridge.call('sync.getScreenHeight', '') || 0
  }

  /**
   * 获取屏幕缩放比例（同步）
   * @returns {number}
   */
  getScreenScale() {
    if (!this.isApp()) {
      return window.devicePixelRatio || 1
    }
    return this.bridge.call('sync.getScreenScale', '') || 1
  }

  /**
   * 获取屏幕亮度（同步）
   * @returns {number}
   */
  getScreenBrightness() {
    if (!this.isApp()) {
      return 1
    }
    return this.bridge.call('sync.getScreenBrightness', '') || 1
  }

  /**
   * 获取状态栏高度（同步）
   * @returns {number}
   */
  getStatusBarHeight() {
    if (!this.isApp()) {
      return 0
    }
    return this.bridge.call('sync.getStatusBarHeight', '') || 0
  }

  /**
   * 检查状态栏是否隐藏（同步）
   * @returns {boolean}
   */
  isStatusBarHidden() {
    if (!this.isApp()) {
      return false
    }
    return this.bridge.call('sync.isStatusBarHidden', '') || false
  }

  // ==================== 网络状态相关（同步方法）====================

  /**
   * 获取网络类型（同步）
   * @returns {string} 'wifi' | 'cellular' | 'none'
   */
  getNetworkType() {
    if (!this.isApp()) {
      return 'unknown'
    }
    return this.bridge.call('sync.getNetworkType', '') || 'unknown'
  }

  /**
   * 检查网络是否可用（同步）
   * @returns {boolean}
   */
  isNetworkAvailable() {
    if (!this.isApp()) {
      return navigator.onLine
    }
    return this.bridge.call('sync.isNetworkAvailable', '') || false
  }

  // ==================== 本地存储相关（同步方法）====================

  /**
   * 获取本地存储（同步）
   * @param {string} key 键名
   * @returns {string|null}
   */
  getLocalStorage(key) {
    if (!this.isApp()) {
      return localStorage.getItem(key)
    }
    return this.bridge.call('sync.getLocalStorage', JSON.stringify({ key })) || null
  }

  /**
   * 设置本地存储（同步）
   * @param {string} key 键名
   * @param {string} value 值
   * @returns {boolean}
   */
  setLocalStorage(key, value) {
    if (!this.isApp()) {
      try {
        localStorage.setItem(key, value)
        return true
      } catch (e) {
        return false
      }
    }
    return this.bridge.call('sync.setLocalStorage', JSON.stringify({ key, value })) || false
  }

  /**
   * 删除本地存储（同步）
   * @param {string} key 键名
   * @returns {boolean}
   */
  removeLocalStorage(key) {
    if (!this.isApp()) {
      try {
        localStorage.removeItem(key)
        return true
      } catch (e) {
        return false
      }
    }
    return this.bridge.call('sync.removeLocalStorage', JSON.stringify({ key })) || false
  }

  /**
   * 清空本地存储（同步）
   * @returns {boolean}
   */
  clearLocalStorage() {
    if (!this.isApp()) {
      try {
        localStorage.clear()
        return true
      } catch (e) {
        return false
      }
    }
    return this.bridge.call('sync.clearLocalStorage', '') || false
  }

  // ==================== 剪贴板相关（同步方法）====================

  /**
   * 获取剪贴板文本（同步）
   * @returns {string|null}
   */
  getPasteboardText() {
    if (!this.isApp()) {
      return null
    }
    return this.bridge.call('sync.getPasteboardText', '') || null
  }

  /**
   * 设置剪贴板文本（同步）
   * @param {string} text 要复制的文本
   * @returns {boolean}
   */
  setPasteboardText(text) {
    if (!this.isApp()) {
      // 降级到 H5 方案
      try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const successful = document.execCommand('copy')
        document.body.removeChild(textarea)
        return successful
      } catch (e) {
        return false
      }
    }
    return this.bridge.call('sync.setPasteboardText', { text }) || false
  }

  // ==================== 系统操作（异步方法）====================

  /**
   * 打开外部 URL
   * @param {string} url URL 地址
   * @returns {Promise<boolean>}
   */
  openUrl(url) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        window.open(url)
        resolve(true)
        return
      }

      this.bridge.call('async.openUrl', { url }, res => {
        const result = this._parseResult(res)
        if (result && result.opened !== undefined) {
          resolve(result.opened)
        } else if (result && result.error) {
          reject(new Error(result.error))
        } else {
          reject(new Error('Open URL failed'))
        }
      })
    })
  }

  /**
   * 打开应用设置页面
   * @returns {Promise<boolean>}
   */
  openAppSettings() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('async.openAppSettings', {}, res => {
        const result = this._parseResult(res)
        if (result && result.opened !== undefined) {
          resolve(result.opened)
        } else if (result && result.error) {
          reject(new Error(result.error))
        } else {
          reject(new Error('Open settings failed'))
        }
      })
    })
  }

  // ==================== 推送通知相关 ====================

  /**
   * 获取推送 Token
   * @returns {Promise<string>}
   */
  getPushToken() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('PushJsApi.getToken', {}, res => {
        const result = this._parseResult(res)
        resolve(result || res)
      })
    })
  }

  /**
   * 请求推送权限
   * @returns {Promise<boolean>}
   */
  requestPushPermission() {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      this.bridge.call('PushJsApi.requestPermission', {}, res => {
        const result = this._parseResult(res)
        resolve(result && result.granted)
      })
    })
  }

  /**
   * 注册推送消息回调
   * @param {Function} callback 回调函数
   */
  onPushMessageReceived(callback) {
    if (!this.isApp()) {
      return
    }

    this.bridge.register('onPushMessageReceived', message => {
      if (typeof callback === 'function') {
        callback(message)
      }
    })
  }

  // ==================== 分享相关 ====================

  /**
   * 分享文本
   * @param {Object} options 配置项
   * @returns {Promise<boolean>}
   */
  shareText(options) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const params = {
        text: options.text || '',
        title: options.title || ''
      }

      this.bridge.call('ShareJsApi.shareText', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(true)
        } else {
          reject(new Error('Share failed'))
        }
      })
    })
  }

  /**
   * 分享图片
   * @param {Object} options 配置项
   * @returns {Promise<boolean>}
   */
  shareImage(options) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const params = {
        imageData: options.imageData || '',
        title: options.title || ''
      }

      this.bridge.call('ShareJsApi.shareImage', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(true)
        } else {
          reject(new Error('Share failed'))
        }
      })
    })
  }

  /**
   * 分享链接
   * @param {Object} options 配置项
   * @returns {Promise<boolean>}
   */
  shareLink(options) {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      const params = {
        url: options.url || '',
        title: options.title || '',
        description: options.description || '',
        thumbnail: options.thumbnail || ''
      }

      this.bridge.call('ShareJsApi.shareLink', params, res => {
        const result = this._parseResult(res)
        if (result && result.success) {
          resolve(true)
        } else {
          reject(new Error('Share failed'))
        }
      })
    })
  }

  // ==================== 定位相关 ====================

  /**
   * 获取当前位置
   * @param {string} accuracy 精度 'high'|'medium'|'low'
   * @returns {Promise<Object>}
   */
  getCurrentLocation(accuracy = 'high') {
    return new Promise((resolve, reject) => {
      if (!this.isApp()) {
        reject(new Error('Not in app'))
        return
      }

      // 检查定位权限
      const hasPermission = this.bridge.call('sync.checkPermission', 'android.permission.ACCESS_FINE_LOCATION')
      if (hasPermission === 'false') {
        this.bridge.call('sync.requestPermission', 'android.permission.ACCESS_FINE_LOCATION')
        reject(new Error('Location permission denied'))
        return
      }

      this.bridge.call('LocationJsApi.getCurrentLocation', { accuracy }, res => {
        const result = this._parseResult(res)
        if (result && result.latitude) {
          resolve(result)
        } else {
          reject(new Error('Get location failed'))
        }
      })
    })
  }

  // ==================== 浏览器相关 ====================

  /**
   * 在应用内打开网页
   * @param {Object} options 配置项
   */
  openInAppBrowser(options) {
    if (!this.isApp()) {
      window.open(options.url)
      return
    }

    const params = {
      url: options.url || '',
      title: options.title || '',
      showToolbar: options.showToolbar !== false
    }

    this.bridge.call('BrowserJsApi.openInApp', params, () => {})
  }

  /**
   * 在外部浏览器打开
   * @param {string} url 网址
   */
  openExternalBrowser(url) {
    if (!this.isApp()) {
      window.open(url)
      return
    }

    this.bridge.call('BrowserJsApi.openExternal', { url }, () => {})
  }

  // ==================== 权限相关 ====================

  /**
   * 检查权限
   * @param {string} permission 权限名称
   * @returns {boolean}
   */
  checkPermission(permission) {
    if (!this.isApp()) {
      return false
    }

    const result = this.bridge.call('sync.checkPermission', permission)
    return result === 'true'
  }

  /**
   * 请求权限
   * @param {string} permission 权限名称
   */
  requestPermission(permission) {
    if (!this.isApp()) {
      return
    }

    this.bridge.call('sync.requestPermission', permission)
  }

  // ==================== 其他工具方法 ====================

  /**
   * 清空浏览器历史
   */
  clearHistory() {
    if (!this.isApp()) {
      return
    }

    this.bridge.call('sync.clearHistory', '')
  }

  /**
   * 退出应用
   */
  exitApp() {
    if (!this.isApp()) {
      return
    }

    this.bridge.call('sync.exitApp', '')
  }

  /**
   * 事件埋点
   * @param {string} event 事件名称
   */
  trackEvent(event) {
    if (!this.isApp()) {
      return
    }

    this.bridge.call('sync.event', event)
  }

  /**
   * 保存数据到原生存储
   * @param {string} key 键
   * @param {string} value 值
   */
  saveToNative(key, value) {
    if (!this.isApp()) {
      return
    }

    const json = { key, value }
    this.bridge.call('sync.save2Sp', JSON.stringify(json))
  }

  /**
   * 从原生存储获取数据
   * @param {string} key 键
   * @returns {string}
   */
  getFromNative(key) {
    if (!this.isApp()) {
      return ''
    }

    return this.bridge.call('sync.get4Sp', key) || ''
  }

  /**
   * 清空原生存储
   */
  clearNativeStorage() {
    if (!this.isApp()) {
      return
    }

    this.bridge.call('sync.clearSp', '')
  }
}

// 导出单例
export default new DSBridgeHelper()
